import type {DefinitionOrReference} from '@github-ui/code-nav'
import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {useEditPermissions} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import linkButtonStyles from '@github-ui/code-view-shared/css/LinkButton.module.css'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import type {PanelType} from '@github-ui/code-view-types'
import type {FileReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import CopilotChatButton from '@github-ui/copilot-code-chat/CopilotCodeLinesMenu'
import type {CopilotInfo} from '@github-ui/copilot-common-types'
import {CopilotPopover} from '@github-ui/copilot-popover'
import {type BannerProps, SpacesSelectionMenu} from '@github-ui/copilot-spaces/components/SpacesSelectionMenu'
import {copyText} from '@github-ui/copy'
import {useCurrentRepository} from '@github-ui/current-repository'
import {NewAgentTaskButton} from '@github-ui/new-agent-task-button/NewAgentTaskButton'
import {Link} from '@github-ui/react-core/link'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ScreenReaderHeading} from '@github-ui/screen-reader-heading'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useAlertTooltip} from '@github-ui/use-alert-tooltip'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useNavigate} from '@github-ui/use-navigate'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {
  CodeSquareIcon,
  CopyIcon,
  DownloadIcon,
  KebabHorizontalIcon,
  PencilIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import type {ButtonProps} from '@primer/react'
import {ActionList, ActionMenu, ButtonGroup, IconButton, LinkButton} from '@primer/react'
import {clsx} from 'clsx'
import {useMemo, useRef} from 'react'

import {useFindInFileOpen} from '../../contexts/FindInFileOpenContext'
import {useSetSearchTerm} from '../../contexts/SearchTermContext'
import {useCurrentBlame} from '../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../hooks/CurrentBlob'
import {BlobDisplayType, useBlobRendererType} from '../../hooks/use-blob-renderer-type'
import {useCopyRawBlobContents} from '../../hooks/use-copy-raw-blob-contents'
import {setKeyboardNavUsed} from '../../hooks/use-cursor-navigation'
import {trackStickyHeader, useStickyHeaderHeight} from '../../hooks/use-sticky-header-height'
import {updateExpandPreferences} from '../../hooks/use-update-panel-expand-preferences'
import {type CopyState, getCopyStateUI} from '../../utilities/Copy'
import AccessibleIconButton from '../AccessibleIconButton'
import type {CodeNavData} from '../blob/BlobContent/BlobContent'
import type {CodeLineData} from '../blob/BlobContent/Code/hooks/use-code-lines'
import FindInFilePopover from '../blob/BlobContent/CodeNav/FindInFilePopover'
import {ContributorAvatars} from '../Contributors'
import styles from './BlobViewHeader.module.css'
import FileNameStickyHeader from './FileNameStickyHeader'
import BlameAgeLegend from './header-components/BlameAgeLegend'
import BlobTabButtons from './header-components/BlobTabButtons'
import CodeSizeDetails from './header-components/CodeSizeDetails'
import EditMenuActionItems from './header-components/EditMenuActionItems'
import {qualifyRef} from './header-components/NavigationMenu'
import {useHasOpenWithItems} from './header-components/OpenWithActionItems'
import RawMenuActionItems, {downloadFile} from './header-components/RawMenuActionItems'
import TableOfContents from './header-components/TableOfContents'
import {StickyLinesHeader} from './StickyLinesHeader'

export default function BlobViewHeader({
  openPanel,
  setOpenPanel,
  showTree,
  validCodeNav,
  treeToggleElement,
  currentStickyLines,
  focusedSearchResult,
  setFocusedSearchResult,
  searchResults,
  searchingText,
  stickyHeaderElement,
  copilotInfo,
  colorizedLines,
  onBannerChange,
}: {
  openPanel: PanelType | undefined
  setOpenPanel: (panel: PanelType | undefined) => void
  showTree: boolean
  validCodeNav: boolean
  treeToggleElement: React.ReactElement
  currentStickyLines: Map<number, CodeLineData>
  focusedSearchResult: number | undefined
  setFocusedSearchResult: (idx: number | undefined) => void
  searchResults: DefinitionOrReference[]
  searchingText: CodeNavData
  stickyHeaderElement: Element | null
  copilotInfo?: CopilotInfo
  colorizedLines?: SafeHTMLString[] | null
  onBannerChange?: (bannerProps: BannerProps | null) => void
}) {
  const setSearchTerm = useSetSearchTerm()
  const payload = useCurrentBlob()
  const symbolsEnabled = payload.symbolsEnabled
  const {isSticky: isStickied} = useStickyObserver(stickyHeaderElement)
  const path = usePath()
  const {copilotAccessAllowed, copilotSpacesEnabled} = useCopilotSettings()
  const refInfo = useRefInfo()
  const {canEdit, canEditOnDefaultBranch} = useEditPermissions()
  const displayType = useBlobRendererType()
  const {sendRepoClickEvent} = useReposAnalytics()
  const stickyHeaderHeight = useStickyHeaderHeight()
  const repository = useCurrentRepository()
  const showAgentTaskButton = payload.copilotSWEAgentEnabled

  const {githubDevUrl} = useReposAppPayload()
  const {
    headerInfo: {toc, onBranch, ghDesktopPath},
    viewable,
  } = payload
  const hasOpenWithItem = useHasOpenWithItems(githubDevUrl, onBranch, ghDesktopPath)

  const blame = useCurrentBlame()

  const onCopy = useCopyRawBlobContents()
  const {createPermalink} = useUrlCreator()
  const {addToast} = useToastContext()

  const {findInFileOpen, setFindInFileOpen} = useFindInFileOpen()
  const rawActionsButtonRef = useRef(null)
  const [updateTooltipMessage, clearTooltipMessage, portalTooltip] = useAlertTooltip(
    'raw-actions-message-tooltip',
    rawActionsButtonRef,
    {direction: 'nw'},
  )

  const {getUrl} = useUrlCreator()
  const blobUrl = getUrl()
  const {isServer} = useRuntimeEnvironment()
  const fileUrl = isServer ? blobUrl : ssrSafeLocation.origin + blobUrl

  const fileReference: FileReference = useMemo(
    () => ({
      type: 'file',
      url: fileUrl,
      path,
      repoID: repository.id,
      repoOwner: repository.ownerLogin,
      repoName: repository.name,
      ref: qualifyRef(refInfo.name, refInfo.refType ?? 'branch'),
      commitOID: refInfo.currentOid,
    }),
    [
      fileUrl,
      path,
      repository.id,
      repository.ownerLogin,
      repository.name,
      refInfo.name,
      refInfo.refType,
      refInfo.currentOid,
    ],
  )

  const editAllowedOnDefaultBranch = viewable && canEditOnDefaultBranch

  const handleCopyFilePath = () => {
    copyText(path)
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'success',
      message: 'Path copied!',
    })
  }

  const handleCopyPermalink = () => {
    copyText(createPermalink({absolute: true}))
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'success',
      message: 'Permalink copied!',
    })
  }

  return (
    <>
      <div ref={trackStickyHeader} className={styles.Box}>
        <div className="react-blob-sticky-header">
          <FileNameStickyHeader isStickied={isStickied} showTree={showTree} treeToggleElement={treeToggleElement} />
        </div>
        <div className={clsx(styles.Box_1, isStickied && styles.Box_1_stickied)}>
          <ScreenReaderHeading as="h2" text="File metadata and controls" />
          <div className={styles.Box_2}>
            <BlobTabButtons />
            <CodeSizeDetails className="react-code-size-details-in-header" />
            <CopilotPopover
              copilotInfo={copilotInfo}
              className="react-code-size-details-in-header"
              view={blame ? 'blame' : 'preview'}
            />
          </div>

          <div className={styles.Box_3}>
            {/* Copy link and permalink shortcut */}
            <GlobalCommands
              commands={{
                'code-view:copy-file-path': handleCopyFilePath,
                'code-view:copy-permalink': handleCopyPermalink,
              }}
            />

            {copilotAccessAllowed && copilotSpacesEnabled && (
              <SpacesSelectionMenu
                repository={repository}
                currentFile={fileReference}
                onBannerChange={onBannerChange}
              />
            )}

            <CopilotChatButton
              copilotAccessAllowed={copilotAccessAllowed}
              messageReference={fileReference}
              hideDropdown
              id="blob-view-header-copilot-icon"
            />

            {showAgentTaskButton && (
              <NewAgentTaskButton
                isIconButton
                size="small"
                tooltipDirection="n"
                currentRepo={repository}
                currentReference={{
                  path,
                }}
                hideRepoSelector
                eventType="new_agent_task_button_codeview"
              />
            )}

            <div className={clsx('react-blob-header-edit-and-raw-actions', styles.Box_4)}>
              <RawGroup onCopy={onCopy} fileName={payload.displayName} />
              <EditMenu />
            </div>
            {displayType === BlobDisplayType.Code && !blame && validCodeNav && symbolsEnabled && (
              <SymbolsButton
                isCodeNavOpen={openPanel === 'codeNav'}
                setCodeNavOpen={open => {
                  if (open) {
                    sendRepoClickEvent('BLOB_SYMBOLS_MENU.OPEN')
                  }
                  // Defer localStorage write to avoid blocking interaction
                  const value = open ? 'codeNav' : ''
                  requestIdleCallback(() => localStorage.setItem('codeNavOpen', value))
                  updateExpandPreferences(null, open, null)
                  setOpenPanel(open ? 'codeNav' : undefined)
                }}
                size="small"
                searchingText={searchingText.selectedText}
              />
            )}
            {!blame && <TableOfContents toc={toc} openPanel={openPanel} setOpenPanel={setOpenPanel} />}

            <div className="react-blob-header-edit-and-raw-actions-combined">
              {portalTooltip}
              <ActionMenu anchorRef={rawActionsButtonRef}>
                <ActionMenu.Anchor>
                  <IconButton
                    icon={KebabHorizontalIcon}
                    tooltipDirection="nw"
                    aria-label="Edit and raw actions"
                    className={clsx('js-blob-dropdown-click', styles.IconButton)}
                    size={'small'}
                    title="More file actions"
                    variant={'invisible'}
                    data-testid="more-file-actions-button"
                    onBlur={clearTooltipMessage}
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay
                  className={clsx('react-blob-header-edit-and-raw-actions-combined', styles.ActionMenu_Overlay)}
                  width="small"
                >
                  <ActionList>
                    {((canEdit && viewable) || editAllowedOnDefaultBranch || hasOpenWithItem) && (
                      <div className="react-navigation-menu-edit-and-raw-actions">
                        <EditMenuActionItems
                          editAllowed={!!(viewable && canEdit) || !!editAllowedOnDefaultBranch}
                          hasOpenWithItem={hasOpenWithItem}
                        />
                        <ActionList.Divider />
                      </div>
                    )}
                    <>
                      <RawMenuActionItems
                        viewable={viewable}
                        onCopy={onCopy}
                        name={payload.displayName}
                        updateTooltipMessage={updateTooltipMessage}
                        all
                      />
                    </>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </div>
          </div>
        </div>
        {blame && (
          <div className={styles.Box_5}>
            <BlameAgeLegend />
            <ContributorAvatars />
          </div>
        )}
      </div>
      {findInFileOpen && (
        <FindInFilePopover
          stickied={isStickied}
          focusedSearchResult={focusedSearchResult}
          setFocusedSearchResult={setFocusedSearchResult}
          searchResults={searchResults}
          onClose={() => {
            setFindInFileOpen(false)
            if (openPanel === 'codeNav') {
              setSearchTerm(searchingText.selectedText)
            }
          }}
        />
      )}
      <div>
        {!blame && currentStickyLines.size > 0 && (
          <div
            style={{
              top: stickyHeaderHeight,
            }}
            className={styles.Box_6}
          >
            <StickyLinesHeader currentStickyLines={currentStickyLines} colorizedLines={colorizedLines} />
          </div>
        )}
      </div>
    </>
  )
}

function EditMenu() {
  const payload = useCurrentBlob()
  const {getUrl} = useUrlCreator()
  const {canEdit, canEditOnDefaultBranch} = useEditPermissions()
  const {sendRepoClickEvent} = useReposAnalytics()
  const {githubDevUrl} = useReposAppPayload()
  const navigate = useNavigate()
  const editAllowedOnDefaultBranch = !!canEditOnDefaultBranch

  const {
    headerInfo: {editTooltip, ghDesktopPath, onBranch},
  } = payload

  const hasOpenWithItem = useHasOpenWithItems(githubDevUrl, onBranch, ghDesktopPath)
  if (!canEdit && !hasOpenWithItem) {
    return null
  }
  const openGitHubDevInNewWindow = () => {
    window.open(githubDevUrl, '_blank')
  }
  const openInGitHubDev = () => {
    //need the pathname because the githubDevUrl is just the root, and cut off the first '/'
    navigate(githubDevUrl + window.location.pathname.substring(1))
  }

  const editUrl = getUrl({action: 'edit'})

  return (
    <>
      <GlobalCommands
        commands={{
          ...(githubDevUrl
            ? {
                'code-view:open-github-dev': openInGitHubDev,
                'code-view:open-github-dev-new-window': openGitHubDevInNewWindow,
              }
            : {}),
          ...(canEdit ? {'code-view:edit-file': () => navigate(editUrl)} : {}),
        }}
      />
      <ButtonGroup>
        {canEdit ? (
          <IconButton
            as={Link}
            tooltipDirection="nw"
            aria-label={editTooltip}
            icon={PencilIcon}
            to={editUrl}
            size="small"
            className={linkButtonStyles.linkButton}
            data-testid="edit-button"
          />
        ) : (
          <AccessibleIconButton icon={PencilIcon} aria-label={editTooltip} disabled />
        )}

        <ActionMenu onOpenChange={open => open && sendRepoClickEvent('BLOB_EDIT_DROPDOWN')}>
          <ActionMenu.Anchor>
            <IconButton
              tooltipDirection="nw"
              icon={TriangleDownIcon}
              size="small"
              aria-label="More edit options"
              data-testid="more-edit-button"
            />
          </ActionMenu.Anchor>

          <ActionMenu.Overlay align="end">
            <ActionList>
              <EditMenuActionItems
                editAllowed={canEdit || editAllowedOnDefaultBranch}
                hasOpenWithItem={hasOpenWithItem}
              />
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </ButtonGroup>
    </>
  )
}

function RawGroup({onCopy, fileName}: {onCopy: () => Promise<CopyState>; fileName: string}) {
  const {
    renderImageOrRaw,
    renderedFileInfo,
    viewable,
    image,
    rawBlobUrl,
    headerInfo: {isGitLfs: isLfs},
  } = useCurrentBlob()
  const navigate = useNavigate()
  const copyButtonRef = useRef(null)
  const [updateTooltipMessage, clearTooltipMessage, portalTooltip] = useAlertTooltip(
    'raw-copy-message-tooltip',
    copyButtonRef,
  )
  const isOnlyPreviewable = !isLfs && ((renderedFileInfo && !viewable) || image)

  const downloadRawFile = async () => {
    await downloadFile(rawBlobUrl, fileName)
  }
  const navToRaw = () => navigate(rawBlobUrl)
  const copyFile = async () => {
    const result = await onCopy()
    const {ariaLabel} = getCopyStateUI(result)
    updateTooltipMessage(ariaLabel)
  }

  const lfsDownloadUrl = new URL(rawBlobUrl, ssrSafeLocation.origin)
  lfsDownloadUrl.searchParams.set('download', '')
  const downloadButtonProps = {
    ['aria-label']: 'Download raw file',
    ['tooltipDirection']: 'n',
    icon: DownloadIcon,
    size: 'small',
    onClick: async () => {
      if (!isLfs) {
        await downloadFile(rawBlobUrl, fileName)
      }
    },
    ['data-testid']: 'download-raw-button',
    // wrapping in tooltip breaks button group styles, manually override borders for now
    className: styles.downloadButton,
  } as const

  return isOnlyPreviewable ? (
    <>
      <GlobalCommands commands={{'code-view:download-raw': downloadRawFile}} />
      <IconButton
        aria-label="Download raw file"
        tooltipDirection="n"
        icon={DownloadIcon}
        size="small"
        onClick={downloadRawFile}
        data-testid="download-raw-button"
      />
    </>
  ) : (
    <>
      <GlobalCommands
        commands={{
          'code-view:view-raw': navToRaw,
          ...(!isLfs && {'code-view:copy-raw': copyFile}),
          'code-view:download-raw': async () => {
            await downloadFile(rawBlobUrl, fileName)
          },
        }}
      />
      <ButtonGroup>
        <LinkButton
          href={rawBlobUrl}
          download={renderImageOrRaw ? 'true' : undefined}
          size="small"
          data-testid="raw-button"
          className={clsx(linkButtonStyles.linkButton, styles.LinkButton)}
        >
          Raw
        </LinkButton>
        {!isLfs && (
          <>
            <IconButton
              ref={copyButtonRef}
              aria-label="Copy raw file"
              tooltipDirection="n"
              icon={CopyIcon}
              size="small"
              onMouseLeave={clearTooltipMessage}
              onClick={copyFile}
              data-testid="copy-raw-button"
              onBlur={clearTooltipMessage}
            />
            {portalTooltip}
          </>
        )}
        {isLfs ? (
          <IconButton as="a" data-turbo="false" href={lfsDownloadUrl.toString()} {...downloadButtonProps} />
        ) : (
          <IconButton {...downloadButtonProps} />
        )}
      </ButtonGroup>
    </>
  )
}

function SymbolsButton({
  isCodeNavOpen,
  setCodeNavOpen,
  size,
  searchingText,
}: {
  isCodeNavOpen: boolean
  setCodeNavOpen: (isOpen: boolean) => void
  size?: ButtonProps['size']
  searchingText: string
}) {
  const shouldShowIndicator = !useCodeViewOptions().openSymbolsOption.enabled && !isCodeNavOpen
  const toggleCodeNav = () => {
    setKeyboardNavUsed(true)
    setCodeNavOpen(!isCodeNavOpen)
  }

  return (
    <>
      <GlobalCommands commands={{'code-view:toggle-symbols': toggleCodeNav}} />
      <IconButton
        aria-label={isCodeNavOpen ? 'Close symbols panel' : 'Open symbols panel'}
        tooltipDirection="nw"
        aria-pressed={isCodeNavOpen}
        aria-expanded={isCodeNavOpen}
        aria-controls="symbols-pane"
        icon={CodeSquareIcon}
        className={clsx(shouldShowIndicator && searchingText ? 'react-button-with-indicator' : '', styles.IconButton_2)}
        onClick={toggleCodeNav}
        variant="invisible"
        data-testid="symbols-button"
        id="symbols-button"
        size={size}
      />
    </>
  )
}
